import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';

import type { IntakeFormFileValue, IntakeFormValues } from '@aline/common/intake-forms/index';
import {
  IntakeFormDefinitionSchema,
  IntakeFormFileValueSchema,
  validateIntakeFormValues,
} from '@aline/common/intake-forms/index';
import { Autocomplete, FormHelperText, Skeleton, Typography } from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import { format, isValid, parseISO } from 'date-fns';
import { AlineInput } from 'src/AlineInput';
import { AlineAutocompleteInput } from 'src/components/AlineAutocompleteInput';
import { AlineNumberInput } from 'src/components/AlineNumberInput';
import { AlineCheckbox } from 'src/components/generic/AlineCheckbox';
import { AlineDatePicker } from 'src/components/generic/AlineDatePicker';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import type { DraftIntakeField, DraftSelectOption } from './intake-form-fields';

/**
 * The default uploader pulls in Filestack + Apollo (and the GQL hook barrel), so
 * it is code-split: surfaces that provide their own `renderFileField` (e.g. the
 * public, token-authed Teams channel-interaction page) never load this chunk,
 * and the authenticated app only fetches it when a file field first renders.
 */
const IntakeFileUploadField = lazy(() =>
  import('./IntakeFileUploadField').then(module => ({ default: module.IntakeFileUploadField }))
);

const useStyles = makeStyles<{ columns: number }>()((theme, { columns }) => ({
  checklist: {
    display: 'flex',
    flexDirection: 'column',
  },
  checklistRow: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    cursor: 'pointer',
    display: 'flex',
    fontSize: 14,
    gap: theme.spacing(0.5),
  },
  /**
   * AlineDatePicker has no `error` prop, so when an intake field is in error we
   * apply the same red border the other (AlineInput-based) controls get. `&&`
   * raises specificity over the picker's own border.
   */
  dateError: {
    '&&': { border: `1px solid ${theme.palette.error.main}` },
    '&&:focus-within': { border: `1px solid ${theme.palette.error.main}` },
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  fullWidth: {
    gridColumn: columns > 1 ? '1 / -1' : undefined,
  },
  helpText: {
    fontSize: 12,
  },
  label: {
    ...theme.appTypography.fieldLabel,
  },
  required: {
    color: theme.palette.error.main,
  },
  root: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
}));

interface IntakeFormFieldsProps {
  fields: DraftIntakeField[];
  onChange: (name: string, value: unknown) => void;
  onRemoveFile: (file: IntakeFormFileValue) => Promise<void>;
  values: IntakeFormValues;
  columns?: number;
  createFiles?: (
    records: Apollo.CreateFilesMutationVariables['input']['records']
  ) => Promise<IntakeFormFileValue[]>;
  disabled?: boolean;
  /**
   * Disable file-upload fields only (other fields stay interactive). Used by the
   * builder preview so "playing around" never triggers a real upload.
   */
  disableFileUpload?: boolean;
  /** Errors returned by a submission endpoint, keyed by the persisted field name. */
  externalFieldErrors?: Record<string, string>;
  /** Persisted form definition (JSON). When present, fields show inline validation. */
  formDefinition?: unknown;
  /** Uploaded files whose document was permanently deleted; shown as deleted. */
  missingFileDocumentIds?: readonly string[];
  /** When true (workflow sent for signature), uploaded files can't be removed. */
  removalLocked?: boolean;
  /**
   * Override for the file-upload control. The default routes uploads through
   * Filestack + Apollo (`IntakeFileUploadField`), which requires the
   * authenticated app shell. Surfaces without it — e.g. the token-authed Teams
   * channel-interaction page — inject their own uploader here.
   */
  renderFileField?: (props: IntakeFileFieldRenderProps) => React.ReactNode;
  /**
   * When this token is non-zero (typically after a failed launch/submit), treat
   * required fields and fields with provided validation errors as touched so
   * inline errors show without per-field blur. Blur history stays in local state.
   */
  showRequiredErrorsToken?: number;
  /** Set false for non-submission previews that should stay visually neutral. */
  showValidation?: boolean;
}

export interface IntakeFileFieldRenderProps {
  onChange: (value: unknown) => void;
  onRemoveFile: (file: IntakeFormFileValue) => Promise<void>;
  value: IntakeFormFileValue[];
  disabled?: boolean;
  fieldName?: string;
  maxFiles?: number;
  missingDocumentIds?: readonly string[];
  removalLocked?: boolean;
}

export function IntakeFormFields({
  columns = 1,
  createFiles,
  disableFileUpload,
  disabled,
  externalFieldErrors,
  fields,
  formDefinition,
  missingFileDocumentIds,
  onChange,
  onRemoveFile,
  removalLocked,
  renderFileField,
  showRequiredErrorsToken,
  showValidation,
  values,
}: IntakeFormFieldsProps) {
  const { classes, cx } = useStyles({ columns });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const shouldValidate = showValidation ?? Boolean(formDefinition);
  /**
   * After a failed submit, treat required / currently-invalid fields as touched
   * for display without mutating blur state (and without an effect).
   */
  const forceShowErrors = Boolean(showRequiredErrorsToken);

  // Parse the (invariant) definition once; only re-validate values on change.
  const parsedDefinition = useMemo(() => {
    if (!shouldValidate || !formDefinition) return null;
    const parsed = IntakeFormDefinitionSchema.safeParse(formDefinition);
    return parsed.success ? parsed.data : null;
  }, [formDefinition, shouldValidate]);

  const validation = useMemo(() => {
    const empty: { missingRequired: Set<string>; provided: Record<string, string> } = {
      missingRequired: new Set(),
      provided: {},
    };
    if (!parsedDefinition) return empty;
    const result = validateIntakeFormValues(parsedDefinition, values);
    return {
      missingRequired: new Set(result.missingRequiredFields),
      provided: result.fieldErrors ?? {},
    };
  }, [parsedDefinition, values]);

  const markTouched = (name: string) =>
    setTouched(prev => (prev[name] ? prev : { ...prev, [name]: true }));

  return (
    <div className={classes.root}>
      {fields.map((field, index) => {
        const fullWidth =
          field.type === 'textarea' || field.type === 'fileUpload' || field.type === 'checklist';
        const helpText = field.helpText?.trim();
        const isTouched =
          Boolean(touched[field.key]) ||
          (forceShowErrors && (field.required || Boolean(validation.provided[field.key])));
        const error =
          externalFieldErrors?.[field.key] ?? getFieldError(field, isTouched, validation);

        return (
          <div
            key={field.key}
            className={cx(classes.field, fullWidth && classes.fullWidth)}
            data-intake-field={index}
            data-required-field={validation.missingRequired.has(field.key) ? '' : undefined}
            onBlur={event => {
              if (getFocusStayedWithinField(event.currentTarget, event.relatedTarget)) return;
              markTouched(field.key);
            }}
          >
            <label className={classes.label}>
              {field.label || 'Untitled field'}
              {field.required && <span className={classes.required}> *</span>}
            </label>
            <FieldControl
              classes={classes}
              createFiles={createFiles}
              disableFileUpload={disableFileUpload}
              disabled={disabled}
              field={field}
              hasError={Boolean(error)}
              missingFileDocumentIds={missingFileDocumentIds}
              removalLocked={removalLocked}
              renderFileField={renderFileField}
              value={values[field.key]}
              onChange={value => onChange(field.key, value)}
              onRemoveFile={onRemoveFile}
            />
            {/* Error replaces helper text so the field doesn't stack both under the control. */}
            {error ? (
              <FormHelperText error>{error}</FormHelperText>
            ) : (
              helpText && (
                <Typography className={classes.helpText} color="textSecondary" variant="body2">
                  {helpText}
                </Typography>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}

function getFocusStayedWithinField(fieldElement: HTMLElement, relatedTarget: EventTarget | null) {
  return relatedTarget instanceof Node && fieldElement.contains(relatedTarget);
}

function getIsBooleanCheckbox(field: DraftIntakeField) {
  return field.type === 'checkbox' || field.source?.type === 'checkbox';
}

interface FieldControlProps {
  classes: {
    checklist: string;
    checklistRow: string;
    dateError: string;
  };
  field: DraftIntakeField;
  onChange: (value: unknown) => void;
  onRemoveFile: (file: IntakeFormFileValue) => Promise<void>;
  value: unknown;
  createFiles?: (
    records: Apollo.CreateFilesMutationVariables['input']['records']
  ) => Promise<IntakeFormFileValue[]>;
  disabled?: boolean;
  disableFileUpload?: boolean;
  hasError?: boolean;
  missingFileDocumentIds?: readonly string[];
  removalLocked?: boolean;
  renderFileField?: (props: IntakeFileFieldRenderProps) => React.ReactNode;
}

function FieldControl({
  classes,
  createFiles,
  disableFileUpload,
  disabled,
  field,
  hasError,
  missingFileDocumentIds,
  onChange,
  onRemoveFile,
  removalLocked,
  renderFileField,
  value,
}: FieldControlProps) {
  /**
   * The visual label is a sibling of the control, so give each control its own
   * accessible name (assistive tech can't infer it from a non-associated label).
   */
  const accessibleName = field.label || 'Untitled field';
  /**
   * Prefer the draft type; fall back to `source` for fields that arrived via
   * API/AI before the builder offered a native control. Values are coerced to
   * the shape the shared schema expects (number / ISO string / bool).
   */
  if (field.type === 'number' || field.source?.type === 'number')
    return (
      <IntakeNumberField
        ariaLabel={accessibleName}
        disabled={disabled}
        hasError={hasError}
        value={value}
        onChange={onChange}
      />
    );
  if (field.type === 'date' || field.source?.type === 'date')
    return (
      <IntakeDateField
        ariaLabel={accessibleName}
        disabled={disabled}
        errorClassName={hasError ? classes.dateError : undefined}
        value={value}
        onChange={onChange}
      />
    );
  if (getIsBooleanCheckbox(field))
    return (
      <label className={classes.checklistRow}>
        <AlineCheckbox
          checked={value === true}
          disabled={disabled}
          size="small"
          onChange={event => onChange(event.target.checked)}
        />
        Yes
      </label>
    );

  switch (field.type) {
    case 'textarea':
      return (
        <AlineInput
          multiline
          aria-label={accessibleName}
          disabled={disabled}
          error={hasError}
          maxRows={10}
          minRows={3}
          placeholder="Add details..."
          value={asText(value)}
          onChange={event => onChange(event.target.value)}
        />
      );
    case 'select': {
      const selectedValue = asText(value);
      const selected =
        field.options.find(option => optionStorageValue(option) === selectedValue) ?? null;
      return (
        <Autocomplete<DraftSelectOption, false, false, false>
          fullWidth
          openOnFocus
          disabled={disabled}
          getOptionLabel={option => option.label}
          isOptionEqualToValue={(option, candidate) =>
            optionStorageValue(option) === optionStorageValue(candidate)
          }
          options={field.options}
          renderInput={params => (
            <AlineAutocompleteInput
              {...params}
              aria-label={accessibleName}
              error={hasError}
              placeholder="Choose an option..."
            />
          )}
          value={selected}
          onChange={(_event, option) => onChange(option ? optionStorageValue(option) : '')}
        />
      );
    }
    case 'checklist': {
      const selected = asStringArray(value);
      return (
        <div className={classes.checklist}>
          {field.options.map((option, index) => {
            const optionValue = optionStorageValue(option);
            const checked = selected.includes(optionValue);
            /**
             * Key by index too: two options can share an effective value
             * (value falls back to label), which would otherwise collide.
             */
            return (
              <label key={`${index}-${optionValue}`} className={classes.checklistRow}>
                <AlineCheckbox
                  checked={checked}
                  disabled={disabled}
                  size="small"
                  onChange={() =>
                    onChange(
                      checked ? selected.filter(v => v !== optionValue) : [...selected, optionValue]
                    )
                  }
                />
                {option.label}
              </label>
            );
          })}
        </div>
      );
    }
    case 'fileUpload': {
      const fileFieldProps: IntakeFileFieldRenderProps = {
        disabled: disabled || disableFileUpload,
        fieldName: field.source?.name ?? field.key,
        maxFiles: field.maxFiles,
        missingDocumentIds: missingFileDocumentIds,
        onChange,
        onRemoveFile,
        removalLocked,
        value: asFileValues(value),
      };
      if (renderFileField) return <>{renderFileField(fileFieldProps)}</>;
      /**
       * The uploader is code-split, so guard both failure modes: a skeleton while
       * the chunk loads, and an error boundary so a chunk-load failure degrades to
       * an inline message instead of blanking (or crashing) the whole form.
       */
      return (
        <ErrorBoundary fallback={<FileUploadUnavailable />}>
          <Suspense fallback={<FileUploadSkeleton />}>
            <IntakeFileUploadField {...fileFieldProps} createFiles={createFiles} />
          </Suspense>
        </ErrorBoundary>
      );
    }
    case 'email':
    case 'text':
    default:
      return (
        <AlineInput
          aria-label={accessibleName}
          disabled={disabled}
          error={hasError}
          placeholder={field.type === 'email' ? 'name@example.com' : 'Type a response...'}
          type={field.type === 'email' ? 'email' : 'text'}
          value={asText(value)}
          onChange={event => onChange(event.target.value)}
        />
      );
  }
}

function FileUploadSkeleton() {
  return <Skeleton height={96} variant="rounded" width="100%" />;
}

function FileUploadUnavailable() {
  return (
    <FormHelperText error>Couldn’t load the file uploader. Refresh to try again.</FormHelperText>
  );
}

/**
 * Number control with a local display buffer. AlineNumberInput/NumberFormat keeps
 * invalid characters out of the input, while the form value stays a finite number
 * (or '' when cleared).
 */
function IntakeNumberField({
  ariaLabel,
  disabled,
  hasError,
  onChange,
  value,
}: {
  ariaLabel: string;
  onChange: (value: unknown) => void;
  value: unknown;
  disabled?: boolean;
  hasError?: boolean;
}) {
  const [text, setText] = useState(() => numberValueToText(value));

  /**
   * Re-sync only when the upstream value no longer matches what the buffer
   * represents, so an external change updates the box but the user's in-progress
   * "1." (which parses to the 1 we already emitted) is left untouched.
   */
  useEffect(() => {
    setText(prev => (numberTextToValue(prev) === value ? prev : numberValueToText(value)));
  }, [value]);

  return (
    <AlineNumberInput
      hideHelperText
      aria-label={ariaLabel}
      disabled={disabled}
      error={hasError}
      placeholder="Enter a number..."
      value={text}
      onValueChange={values => {
        setText(values.formattedValue);
        onChange(typeof values.floatValue === 'number' ? values.floatValue : '');
      }}
    />
  );
}

function numberValueToText(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string') return value;
  return '';
}

/** '' for a blank field, otherwise a number (NaN when the text isn't numeric). */
function numberTextToValue(text: string): number | '' {
  return text.trim() === '' ? '' : Number(text);
}

/**
 * Date control backed by the shared AlineDatePicker. Intake values are stored as
 * `YYYY-MM-DD` strings (the schema's date shape), so this adapts to/from a Date
 * and clears to '' when emptied. Parsing/formatting use local time so the picked
 * day round-trips without a timezone off-by-one. `errorClassName` reddens the
 * border in sync with the other controls (the picker has no `error` prop).
 */
function IntakeDateField({
  ariaLabel,
  disabled,
  errorClassName,
  onChange,
  value,
}: {
  ariaLabel: string;
  onChange: (value: unknown) => void;
  value: unknown;
  disabled?: boolean;
  errorClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <AlineDatePicker
      dateFieldProps={{ 'aria-label': ariaLabel, fullWidth: true }}
      disabled={disabled}
      enableFlip={true}
      inputClassName={errorClassName}
      open={open}
      setOpen={setOpen}
      value={parseStoredDate(value)}
      onDateChange={date => onChange(date && isValid(date) ? format(date, 'yyyy-MM-dd') : '')}
    />
  );
}

function parseStoredDate(value: unknown): Date | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

/** A select/checklist option stores its `value`, falling back to its `label`. */
function optionStorageValue(option: DraftSelectOption): string {
  return option.value || option.label;
}

/**
 * Inline error for a field, only shown once the field has been blurred. A
 * non-empty but malformed value (a bad email, an out-of-set option) reports the
 * shared schema's specific message; otherwise a required field the validator
 * still considers unsatisfied reports "required". That fallback covers values
 * getIsEmptyValue treats as present but the validator rejects — e.g. a required
 * checkbox left unchecked, which stores false rather than an empty value.
 */
function getFieldError(
  field: DraftIntakeField,
  touched: boolean,
  validation: { missingRequired: Set<string>; provided: Record<string, string> }
): string | undefined {
  if (!touched) return undefined;
  const providedError = validation.provided[field.key];
  if (providedError) return providedError;
  if (validation.missingRequired.has(field.key)) return 'This field is required.';
  return undefined;
}

function asText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function asFileValues(value: unknown): IntakeFormFileValue[] {
  if (!Array.isArray(value)) return [];
  const files: IntakeFormFileValue[] = [];
  for (const item of value) {
    const parsed = IntakeFormFileValueSchema.safeParse(item);
    if (parsed.success) files.push(parsed.data);
  }
  return files;
}
